import { Invoice, SPCode } from "../../../types/common";
import {
  FIXED_DISCOUNT_PROMOTION,
  FREE_TRIAL_PROMOTION,
  NOT_DEFINED_PROMOTION,
  NOW_TIMEFRAME,
  PERCENT_DISCOUNT_PROMOTION,
  RENEWAL_TIMEFRAME,
} from "./constants";
import { BillingToken, ICaptchaToken, IPromotionCode } from "../user/types";

export interface ISubscriptionUpcoming {
  sku_id: string;
  applies_at: string;
  addons: ISubscriptionAddon[];
  sku: ISubscriptionEmbeddedSku;
}

export interface ISpSubscriptionUpcoming {
  sp_sku_code: string;
  sp_sku_name: string;
  applies_at: string;
}

export interface ISpSubscriptionBasic {
  sp_code: SPCode;
  sp_customer_id: string;
  sp_subscription_id: string;
  is_active: boolean;
}

// Fields every promotion detail carries, whatever its kind.
export interface ISubscriptionPromotionDetail {
  // PH_SUBSCRIPTION sends null when the discount's duration cannot be expressed
  // as an ISO-8601 span (e.g. a discount that runs until a fixed date).
  duration: string | null;
}

export interface IPirCentDetail extends ISubscriptionPromotionDetail {
  kind: typeof PERCENT_DISCOUNT_PROMOTION;
  percent: number;
}

export interface IFixedDetail extends ISubscriptionPromotionDetail {
  kind: typeof FIXED_DISCOUNT_PROMOTION;
  amount: IMoney;
}

export interface IFreeDetail extends ISubscriptionPromotionDetail {
  kind: typeof FREE_TRIAL_PROMOTION;
  length: string;
  is_trial_expired: boolean;
}

// ph-api's fallback when the provider's coupon kind maps to nothing we model:
// a promotion is on the subscription, but its size is not stated.
export interface INotDefinedDetail extends ISubscriptionPromotionDetail {
  kind: typeof NOT_DEFINED_PROMOTION;
}

// Discriminated on `kind`: narrowing on `detail.kind` reads a variant's fields with no
// hand-written guards or casts.
export type PromotionDetail =
  | IFreeDetail
  | IPirCentDetail
  | IFixedDetail
  | INotDefinedDetail;

export interface ISpSubscriptionPromotion {
  code: string;
  // Optional: a promotion can arrive without a detail (and the runtime guards
  // already defend against that), so the type says so rather than lying.
  detail?: PromotionDetail | null;
  is_active: boolean;
}

export interface ISpSubscription extends ISpSubscriptionBasic {
  billing_period: string;
  // The rail the subscription is billed on ("crypto"). `sp_code` names the
  // provider that OWNS the subscription — PH_SUBSCRIPTION covers everything the
  // subscription service bills — so only this says what the customer paid with.
  // Absent for providers whose sp_code already names the instrument (Recurly,
  // the app stores) and for any provider that does not state one.
  payment_provider?: string | null;
  // `next_invoice_list_amount` present is the discriminator: then this is the CHARGED
  // amount (post-discount, PH subscription service); absent (Recurly, app stores), it's the
  // PRE-discount charge and the portal computes the discount from `promotion` itself.
  next_invoice_amount: IMoney | null;
  // The provider's own breakdown (PH subscription service only): pre-discount price, and the
  // cut it will actually apply (already rounded — don't re-derive it). All three travel
  // together — an unvouched breakdown answers with nulls, never a partial one.
  next_invoice_list_amount?: IMoney | null;
  next_invoice_discount_amount?: IMoney | null;
  upcoming: ISpSubscriptionUpcoming | null;
  promotion?: ISpSubscriptionPromotion;
  canceled_at?: string;
  terminated_at?: string;
  invoiced_at: string;
  next_invoices_at: string;
  created_at: string;
  updated_at: string;
  addons?: any[];
  states: ISpSubscriptionStates[];
  paused_at?: string;
  will_resume_at?: string;
}

export enum ISpSubscriptionStates {
  canceled = "CANCELED",
  changing = "CHANGING",
  future = "FUTURE",
  intro = "INTRO",
  pastDue = "PAST_DUE",
  paused = "PAUSED",
  pending = "PENDING",
  refunded = "REFUNDED",
  terminated = "TERMINATED",
  trial = "TRIAL",
}

export interface ISubscriptionDetails {
  customer_id: string;
  subscription_id: string;
  // Declared required because 22 call sites read it bare and widening it to
  // optional breaks the build. It is still server data: the detail of a
  // subscription whose SP-side row is gone (e.g. after an admin unlink) comes
  // back without it, so the `?.` guards the selectors put around it are
  // deliberate and must not be tidied away as dead code just because this
  // declaration no longer justifies them.
  sp: ISpSubscription;
  upcoming?: ISubscriptionUpcoming | null;
  addons?: any[];
}

export interface ISubscriptionAddon {
  subscription_id: string;
  sku_id: string;
  created_at: string;
  updated_at: string;
  quantity: number;
  sku: ISubscriptionEmbeddedSku;
}

export interface IProductEntitlement {
  product_code: string;
  feature_code: string;
  service_code: string;
  value: number;
}

export interface IProduct {
  code: string;
  entitlements: IProductEntitlement[];
  created_at: string;
  updated_at: string;
}

export interface ISubscriptionEmbeddedSku {
  licenses: number;
  product: IProduct;
  product_line_code: string;
}

export interface ISubscription {
  id: string;
  customer_id: string;
  principal_id?: string;
  is_terminated: boolean;
  is_good_standing: boolean;
  is_active: boolean;
  offering_code: string;
  sku_id: string;
  sp_code: SPCode;
  sp_subscription_id: string;
  will_change_at?: string;
  // ph-api writes this only for a Cancelled provider state and clears it to null
  // otherwise (SubscriptionStandingService.UpdateSubscriptionAsync), so null —
  // not just absence — is what actually arrives for a running subscription.
  // The type says `string | undefined` because widening it to `| null` breaks
  // consumers; the null checks around every read of it are deliberate and must
  // not be tidied away as dead code.
  will_expire_at?: string;
  created_at: string;
  updated_at: string;
  sku?: ISubscriptionEmbeddedSku;
  addons?: ISubscriptionAddon[];
  product_line_code?: string;
}

export interface ISubscriptionsResponse {
  items: ISubscription[];
}

export interface IMoney {
  amount: number;
  currency: string;
}

export interface IMoneyTax extends IMoney {
  tax: number;
}

export interface ICreateSubscriptionRequest {
  sku_id: string;
  offering_code?: string;
  currency?: string;
  billing_token?: BillingToken;
  promotion_token?: IPromotionCode;
  captcha_token?: ICaptchaToken;
}

export interface ISpAddon {
  sku_id: string;
  quantity: number;
}

export interface ISubscriptionRequest {
  customerId: string;
  subscriptionId: string;
}

// GET .../actions/renewal-invoice (PH_SUBSCRIPTION only): whether the customer
// may pay their next renewal right now, and the live (still open) renewal
// invoice for the current period when one has already been minted.
export interface IRenewalInvoiceStatus {
  payable: boolean;
  // Start of the payment window (period end minus the renewal-notice days), or
  // null when the subscription can never be renewed again.
  payable_from: string | null;
  // End of the grace period, or null when no grace period applies.
  payable_until: string | null;
  // The provider's own status verbatim (`active`, `past_due`, `cancelled`, ...).
  // This is the portal's only *fresh* reading of "is this renewal overdue": the
  // subscription payload says `is_active: true` throughout the grace period, so
  // it cannot answer that on its own. Optional because ph-api types it nullable.
  subscription_status?: string | null;
  invoice: Invoice | null;
}

export interface IUpdateSubscriptionRequest {
  sku_id: string;
  addons?: ISpAddon[] | null;
  promotion_token?: IPromotionCode;
}

export interface IUpdateSubscriptionResponse {
  timeframe: string;
}

export interface ICancelSubscriptionRequest {
  customerId: string;
  subscriptionId: string;
  cancelReason: string;
}

export interface ICancelSubscriptionResponse {
  timeframe: typeof NOW_TIMEFRAME | typeof RENEWAL_TIMEFRAME;
  applies_at?: string;
}

interface IOptionSchema {
  description: string;
  type: string;
  properties: any;
  required: string[];
}

export interface ISubscriptionOptions {
  options: { auto_recycle: true };
  created_at: string;
  updated_at: string;
  schema: IOptionSchema;
  eTag: string;
}

export interface ISubscriptionPreviewDetails {
  credit_applied: IMoney;
  subtotal: IMoney;
  paid: IMoney;
  prorated_credit: IMoney;
  discount: IDiscount;
  net_prorated_charge: IMoney;
  net_prorated_tax: IMoney;
  net_discount: IMoney;
}

export interface ISubscriptionPreview {
  estimate: IMoneyTax;
  is_credit?: boolean;
  timeframe?: typeof NOW_TIMEFRAME | typeof RENEWAL_TIMEFRAME;
  details: ISubscriptionPreviewDetails;
}

export interface IDiscount {
  amount: number;
  currency: string;
}

export const THIRD_PARTY_OFFERING: Array<string> = ["VYPRVPN_SEALAND_2024Q3"];
